import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { FaCommentDots } from 'react-icons/fa6';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useFeature } from '../../hooks/useFeature';
import { useLogin } from '../../hooks/useLogin';
import { useVerificationWall } from '../../hooks/useVerificationWall';
import { useUpdates } from '../../hooks/useUpdates';
import {
  useMessageThread, useUnreadMessageCount, useSendMessage, useMarkMessagesRead, invalidateMessages,
} from '../../api/queryHooks/messages';
import { isStaff } from '../../auth/roles';
import ChatPanel from './ChatPanel';
import { useChatTopOffset } from './useChatTopOffset';
import './chat.scss';

const ChatIndicator = () => {
  const [ open, setOpen ] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  const [ searchParams, setSearchParams ] = useSearchParams();

  const messagingEnabled = useFeature('messaging');
  const { userData, isLoggedIn, isGuest } = useLogin();
  // The chat is the buyer's channel to the shop; staff never see the buyer widget.
  const isStaffUser = isStaff(userData?.roles);
  // A user who still has to verify their email is refused every /messages call, like a guest.
  const { pending } = useVerificationWall();
  const visible = messagingEnabled && isLoggedIn && !isGuest && !pending && !isStaffUser;

  const { data: thread, isError: threadError } = useMessageThread(open);
  // Only poll unread counts for users who can actually see the chat button —
  // otherwise every logged-in admin/guest would fire GET /messages/unread and get a 403.
  const { data: unread } = useUnreadMessageCount(visible);
  const send = useSendMessage();
  const markRead = useMarkMessagesRead();

  // Real page-visibility tracking (initialized from the current value so a
  // panel opened on an already-backgrounded tab — unlikely, but possible via
  // the deeplink below racing a tab switch — doesn't start out wrong).
  const [ tabVisible, setTabVisible ] = useState(() => document.visibilityState === 'visible');
  useEffect(() => {
    const onVisibilityChange = () => setTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Single source of truth for "mark read": fires whenever the panel is open,
  // the tab is genuinely foregrounded, and there is something unread —
  // regardless of *why* any of those just became true. That covers every
  // path that used to need its own separate call (clicking to open the
  // panel; a message.created arriving over the socket while already open;
  // the ?chat=1 deeplink opening the panel straight to `open: true`; the tab
  // regaining focus while a reply arrived in the background; and a
  // reconnect's invalidateMessages() surfacing a count that changed while
  // disconnected) without each of those sites having to remember to call
  // markRead itself — which is exactly how the WS-arrives-while-open path
  // (#17) and the deeplink path got missed before. `document.visibilityState`
  // used to be sampled once inside the WS handler and never rechecked; now
  // it's real state kept current by the listener above, so backgrounding the
  // tab actually blocks the call (not just at the instant a message arrives)
  // and foregrounding it later actually recovers, no manual close/reopen
  // needed. The POST is idempotent (a plain `update_all` — see
  // MessagesController#read), and TanStack Query does not dedupe concurrent
  // mutations, so this can double-fire (e.g. both the mobile and desktop
  // ChatIndicator instances TopNav mounts will each run this) — accepted
  // rather than adding cross-instance coordination for an idempotent no-op.
  useEffect(() => {
    if (open && tabVisible && (unread?.count ?? 0) > 0) markRead.mutate();
    // markRead is intentionally omitted: it's a fresh object every render
    // (TanStack Query), and re-running this effect only needs to track the
    // three values actually being compared above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tabVisible, unread?.count]);

  useUpdates(
    useCallback((msg) => {
      if (msg.type === 'message.created') invalidateMessages();
    }, []),
    // Recovery after a socket drop: a reply that arrived while disconnected
    // is neither in the (now-stale) thread cache nor reflected in the unread
    // badge until something refetches. Reuses the same invalidate the live
    // path uses; the mark-read effect above then reacts to whatever count
    // that refetch turns up exactly as it would for a live message.
    useCallback(() => invalidateMessages(), []),
  );

  // message.received emails deep-link to `/?chat=1` (the literal is pinned by
  // a backend spec — do not change it). Consumed once `visible` — not just
  // once on mount: `ChatIndicator` stays mounted (TopNav renders it
  // unconditionally) even for a logged-out visitor, and login here doesn't
  // remount the layout, so consuming eagerly would strip the param and latch
  // `open: true` on a component that isn't shown yet, popping the panel open
  // at an arbitrary later moment once the account features that gate
  // `visible` finish loading. Waiting for `visible` also means a user who
  // clicks the link before logging in still gets the panel once they do.
  const chatParamConsumed = useRef(false);
  useEffect(() => {
    if (!visible || chatParamConsumed.current) return;
    if (searchParams.get('chat') !== '1') return;
    chatParamConsumed.current = true;
    setOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('chat');
      return next;
    }, { replace: true });
  }, [visible, searchParams, setSearchParams]);

  // On mobile the panel is a full-height sheet that must sit directly below
  // the sticky top nav — see useChatTopOffset (shared with ReminderBell).
  useChatTopOffset(open, ref);

  const toggle = () => setOpen((o) => !o);

  if (!visible) return null;
  const count = unread?.count ?? 0;

  return (
    <div ref={ref}>
      <div
        className="chat-indicator"
        onClick={toggle}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle()}
        aria-label="Messages"
        role="button"
        tabIndex={0}
      >
        <div className="chat-icon-wrapper">
          <FaCommentDots size={20} />
          {count > 0 && <span className="chat-badge">{count}</span>}
        </div>
      </div>
      {open && (
        <ChatPanel
          messages={thread ?? []}
          onSend={(b) => send.mutate(b)}
          sending={send.isPending}
          error={threadError}
        />
      )}
    </div>
  );
};

export default ChatIndicator;
